export interface InvestSection {
  id: string;
  title: string;
  content: string;
  image: string;
  imageAlt: string;
}

export interface InvestStat {
  label: string;
  value: string;
}

export interface InvestCard {
  id: string;
  title: string;
  description: string;
  image: string;
}

/**
 * Investment Fundamentals intro copy — the lead-in paragraph
 * sourced from docs/site-info/invest-info.md.
 */
export const investIntro =
  'In 2025, the Cayman Islands residential market continued its transition toward a more mature and resilient phase, with key indicators normalising and volatility easing. Condominium prices increased by 5% year over year, in line with 2024 and well below the rapid growth recorded between 2018 and 2023\u2014signalling a healthier, more sustainable pricing environment for long-term investors.';

export const investIntroSecondary =
  'Against this backdrop, total residential transaction value reached a record $1 billion, the highest annual total ever recorded, highlighting continued confidence in Cayman real estate at the premium end of the market. Properties priced above $2.4 million represented just 12% of transactions but nearly 50% of total sales value, reinforcing the depth of demand for high-quality, well-positioned assets.';

export const investIntroPearl =
  'As a boutique, high-specification development, The Pearl is ideally aligned with this premium segment, offering investors exposure to a market that continues to attract capital even as it matures.';

/**
 * Detailed investment sections shown as alternating image-text rows.
 * Images sourced from unsplash.com — for production, download to /public/img/invest/.
 */
export const investSections: InvestSection[] = [
  {
    id: 'rental-yield',
    title: 'Strong Rental Yield Potential',
    content:
      'Rental conditions in 2025 remained stable and resilient, with rents increasing by approximately 2%, broadly in line with inflation. While net rental yields (after strata and insurance) remained below 6% for the fifth consecutive year, the consistency of tenant demand reflects Cayman\u2019s lifestyle-led appeal and the island\u2019s limited supply of new, high-quality condominiums.\n\nNew condo supply declined for a third consecutive year, supporting long-term rental fundamentals for developments that combine location, design, and amenity quality. The Pearl\u2019s luxury finishes, modern layouts, and lifestyle-focused amenities position it to attract quality tenants and deliver reliable, inflation-linked rental income in a tightening supply environment.',
    image: '/img/rental.jpg',
    imageAlt: 'Modern luxury living room with expansive windows',
  },
  {
    id: 'location',
    title: 'Location Fundamentals',
    content:
      'As residential markets mature, performance becomes increasingly location-driven. In 2025, price growth along Seven Mile Beach underperformed the broader market\u2014a pattern commonly observed in later-cycle conditions\u2014while well-connected, amenity-rich neighbourhoods continue to stand out for their long-term fundamentals.\n\nLocated in Grand Harbour, The Pearl benefits from excellent connectivity, proximity to key business districts, and easy access to retail, dining, and marina facilities. Ongoing development and infrastructure investment in the area further support capital preservation and long-term value appreciation, making Grand Harbour one of Cayman\u2019s most compelling residential locations for discerning investors.',
    image: '/img/7-mile-beach.jpg',
    imageAlt: 'Harbour marina with luxury boats and waterfront',
  },
  {
    id: 'management',
    title: 'Premium Property Management',
    content:
      'Market liquidity softened modestly in 2025, with average days on market increasing from 133 to 147 and pricing negotiations becoming more measured. In a more selective and normalised market, professional property management is a critical differentiator in protecting asset value and maximising rental performance.\n\nThe Pearl is supported by comprehensive, premium property management services that handle every aspect of ownership\u2014from tenant placement and rent collection to maintenance and ongoing asset care. This ensures your investment remains well-maintained, competitively positioned, and optimised for returns, allowing owners to enjoy a truly hands-off investment experience.',
    image: '/img/premium-property.jpg',
    imageAlt: 'Well-maintained luxury home exterior with landscaping',
  },
];

/**
 * Key 2025 market statistics for the snapshot section.
 */
export const investStats: InvestStat[] = [
  { label: 'Condo Price Growth', value: '5% YoY' },
  { label: 'Record Transactions', value: '$1B' },
  { label: 'Premium Segment Share', value: '~50%' },
  { label: 'Rent Growth', value: '~2%' },
];

/**
 * "Why Invest" benefit cards with image headers.
 * Images sourced from unsplash.com — for production, download to /public/img/invest/.
 */
export const investCards: InvestCard[] = [
  {
    id: 'location',
    title: 'Prime Location',
    description:
      'Grand Harbour offers excellent connectivity, proximity to business districts, and access to retail, dining, and marina facilities\u2014one of Cayman\u2019s most compelling residential locations.',
    image: '/img/prime-location.jpg',
  },
  {
    id: 'lifestyle',
    title: 'Lifestyle-Led Appeal',
    description:
      'Declining condo supply and consistent tenant demand position The Pearl\u2019s luxury finishes and lifestyle amenities to attract quality tenants in a tightening market.',
    image: '/img/lifestyle.jpg',
  },
  {
    id: 'growth',
    title: 'Growth Potential',
    description:
      'With 5% annual price growth and record $1B in transactions, Cayman\u2019s maturing market continues to attract capital\u2014especially in the premium segment.',
    image: '/img/growth.jpg',
  },
  {
    id: 'management',
    title: 'Hands-Off Investment',
    description:
      'Comprehensive property management handles every aspect of ownership\u2014from tenant placement to ongoing asset care\u2014so you can enjoy a truly hands-off experience.',
    image: '/img/management.jpg',
  },
];
